import React, { useEffect, useState } from "react";
import styled from "@emotion/styled";
import * as Yup from "yup";
import { useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { Formik } from "formik";
import {
  Grid,
  Modal,
  Typography,
  useMediaQuery,
  CircularProgress,
  Box as MuiBox,
  IconButton as MuiIconButton,
  TextField,
} from "@mui/material";

import useZone from "../../../../hooks/user/useZone";
import useAuth from "../../../../hooks/useAuth";
import { UserType } from "../../../../utils/constants";

import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";
import { ReactComponent as CancelIcon } from "../../../../vendor/button/cancel.svg";
import { ReactComponent as ConfirmIcon } from "../../../../vendor/button/confirm.svg";

import { Alert, IOSSwitch, ModalButton } from "../../common/styled";
import ModalBox from "../../../common/ModalBox";

const Root = styled.div`
  justify-content: center;
  align-items: center;
  display: flex;
  min-height: 200px;
`;
const IconButton = styled(MuiIconButton)`
  padding: 16px;
  svg {
    width: 24px;
    height: 24px;
  }
`;

function ZoneModal({ open, handleClose, zone }) {
  const navigate = useNavigate();
  const { setZoneController, userType } = useAuth();
  const { updateZone, getZones } = useZone();

  const theme = useTheme();
  const isMD = useMediaQuery(theme.breakpoints.up("md"));

  const [subDomainList, setSubDomainList] = useState([{ name: "", addr: "", enabled: true }]);
  const [enableLLM, setEnableLLM] = useState(true);
  useEffect(() => {
    if (zone !== null) {
      if (zone.subdomains.length === 0) {
        setSubDomainList([{ name: "", addr: "", enabled: true }]);
      } else {
        var tmpArr = [];
        for (let index = 0; index < zone.subdomains.length; index++) {
          tmpArr.push({
            name: zone.subdomains[index]["name"],
            addr: zone.subdomains[index]["addr"],
            enabled: zone.subdomains[index]["enabled"],
          });
        }
        setSubDomainList(tmpArr);
      }
      setEnableLLM(zone.enabled);
    }
  }, [zone]);

  // handle input change
  const handleNameInputChange = (e, index) => {
    const { value } = e.target;
    // Not allow space in value
    if (/\s/g.test(value)) {
      return;
    }
    const list = [...subDomainList];
    list[index].name = value;
    setSubDomainList(list);
  };

  const handleAddrInputChange = (e, index) => {
    const { value } = e.target;
    // Not allow space in value
    if (/\s/g.test(value)) {
      return;
    }
    const list = [...subDomainList];
    list[index].addr = value;
    setSubDomainList(list);
  };

  // handle click event of the Remove button
  const handleRemoveClick = (index) => {
    const list = [...subDomainList];
    list.splice(index, 1);
    setSubDomainList(list);
  };

  // handle click event of the Add button
  const handleAddClick = () => {
    setSubDomainList([...subDomainList, { name: "", addr: "", enabled: true }]);
  };

  const llmEnableChange = (event) => {
    if (UserType.ORG_USER === userType) return;
    const _enableLLM = !enableLLM;
    setEnableLLM(_enableLLM);
  };

  const subdomainEnableChange = (event) => {
    if (UserType.ORG_USER === userType) return;
    const list = [...subDomainList];
    list.forEach((x) => {
      if (x.name === event.target.value) {
        x.enabled = !x.enabled;
      }
    });
    setSubDomainList(list);
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        zone_id: zone?.zone_id ? zone.zone_id : "",
        zone_name: zone?.name ? zone.name : "",
        enable: undefined !== zone?.enabled ? zone.enabled : true,
        zone_addr: zone?.addr ? zone.addr : "",
        zone_path: zone?.path ? zone.path : "",
      }}
      validationSchema={Yup.object().shape({
        zone_id: Yup.string()
          .matches(/^(?!www\.).*$/, 'Remove "www" label')
          .required("Domain is required"),
        zone_name: Yup.string().required("Name is required"),
        zone_addr: Yup.string().required("Address is required"),
        zone_path: Yup.string().required("Path is required"),
      })}
      onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
        if (0 < subDomainList.length && !subDomainList[subDomainList.length - 1].name) {
          subDomainList.splice(subDomainList.length - 1, 1);
        }
        try {
          values.submit = undefined;
          values.enable = enableLLM;
          await updateZone(zone.id, values, subDomainList);
          getZones(setZoneController);
          handleClose();
          resetForm();
          navigate("/llm/zones");
        } catch (error) {
          const message = error.message || "Something went wrong";
          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
        if (0 === subDomainList.length) {
          setSubDomainList([{ name: "", addr: "", enabled: true }]);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, isSubmitting, touched, values }) => (
        <React.Fragment>
          <Modal
            open={open}
            onClose={(_, reason) => {
              if (reason !== "backdropClick") {
                handleClose();
              }
            }}
          >
            <ModalBox
              sx={{ width: isMD ? "960px" : "90vw" }}
              title={null === zone ? "LLM Zone" : `LLM Zone - ${zone.zone_id} ( + www )`}
              handleClose={handleClose}
            >
              <form noValidate onSubmit={handleSubmit}>
                {zone === null ? (
                  <>
                    <Root>
                      <CircularProgress color="primary" />
                    </Root>
                  </>
                ) : (
                  <>
                    <Grid container pt={4}>
                      <Grid item xs={12}>
                        {errors.submit && (
                          <Alert mt={2} mb={3} variant="outlined" severity="error">
                            {errors.submit}
                          </Alert>
                        )}
                      </Grid>

                      <Grid item xs={12} md={4} pr={4} pb={4} sx={{ margin: "auto", textAlign: "left" }}>
                        <Typography py={4} px={4}>
                          Origin Address
                        </Typography>
                      </Grid>
                      <Grid item xs={9} md={6} pb={4}>
                        <TextField
                          color="secondary"
                          name="zone_addr"
                          value={values.zone_addr}
                          fullWidth
                          error={Boolean(touched.zone_addr && errors.zone_addr)}
                          helperText={touched.zone_addr && errors.zone_addr}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={3} md={2} pb={4} display="flex" alignItems="center" justifyContent={"center"}>
                        <IOSSwitch checked={enableLLM} onChange={llmEnableChange} color="success" />
                      </Grid>
                      <Grid item xs={12} md={4} pr={4} pb={4} sx={{ margin: "auto", textAlign: "left" }}>
                        <Typography py={4} px={4}>
                          Zone Path
                        </Typography>
                      </Grid>
                      <Grid item xs={9} md={6} pb={4}>
                        <TextField
                          color="secondary"
                          name="zone_path"
                          value={values.zone_path}
                          fullWidth
                          error={Boolean(touched.zone_path && errors.zone_path)}
                          helperText={touched.zone_path && errors.zone_path}
                          onBlur={handleBlur}
                          onChange={handleChange}
                        />
                      </Grid>
                      <Grid item xs={3} md={2} />
                      <Grid item xs={12} sx={{ margin: "auto", textAlign: "left" }}>
                        <Typography variant="captionBold" py={8} px={4}>
                          Sub Domains
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography py={4} px={4}>
                          Host Name
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography py={4} px={4}>
                          Origin Address
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography py={4} px={4}>
                          Enable LLM Guardrail
                        </Typography>
                      </Grid>
                      <Grid item xs={12} pb={4}>
                        <Grid container>
                          {subDomainList?.map((x, i) => {
                            return (
                              <>
                                <Grid item xs={12} md={4} px={2} pb={2}>
                                  <MuiBox display="flex" alignItems="center">
                                    <TextField
                                      color="secondary"
                                      name="subDomain"
                                      value={x?.name}
                                      onChange={(e) => handleNameInputChange(e, i)}
                                      sx={{ width: "100%" }}
                                    ></TextField>
                                    <Typography px={2}>.{zone.zone_id}</Typography>
                                  </MuiBox>
                                </Grid>
                                <Grid item xs={12} md={4} px={2} pb={2}>
                                  <TextField
                                    color="secondary"
                                    name="subDomain"
                                    value={x?.addr}
                                    onChange={(e) => handleAddrInputChange(e, i)}
                                    sx={{ width: "100%" }}
                                  ></TextField>
                                </Grid>
                                <Grid item xs={12} md={4}>
                                  <Grid container>
                                    <Grid item xs={6} display="flex" alignItems="center" justifyContent={"center"}>
                                      <IOSSwitch
                                        checked={x?.enabled}
                                        disabled={x?.name?.length === 0}
                                        value={x?.name}
                                        onChange={subdomainEnableChange}
                                        px={4}
                                        color="success"
                                      />
                                    </Grid>
                                    <Grid item xs={6}>
                                      {subDomainList.length !== 1 && (
                                        <IconButton size="large" onClick={() => handleRemoveClick(i)}>
                                          <RemoveIcon />
                                        </IconButton>
                                      )}
                                      {subDomainList.length - 1 === i && (
                                        <IconButton size="large" onClick={handleAddClick} disabled={x?.name?.length === 0}>
                                          <AddIcon />
                                        </IconButton>
                                      )}
                                    </Grid>
                                  </Grid>
                                </Grid>
                              </>
                            );
                          })}
                        </Grid>
                      </Grid>
                      <Grid item xs={12} textAlign={"right"}>
                        <ModalButton variant="outlined" color="primary" onClick={handleClose} mr={4} startIcon={<CancelIcon />}>
                          Cancel
                        </ModalButton>
                        <ModalButton type="submit" variant="contained" color="success" disabled={isSubmitting} startIcon={<ConfirmIcon />}>
                          Save
                        </ModalButton>
                      </Grid>
                    </Grid>
                  </>
                )}
              </form>
            </ModalBox>
          </Modal>
        </React.Fragment>
      )}
    </Formik>
  );
}

export default ZoneModal;
